import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { DotsVerticalIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import './Global.css';

const AutoCloseConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [deletingConfig, setDeletingConfig] = useState(false);
  const [entryBlocking, setEntryBlocking] = useState({ enabled: false, marginMinutes: 10 });
  const [savingBlocking, setSavingBlocking] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    closeTime: '',
    entryTimeMin: '',
    entryTimeMax: '',
    earlyEntryMinutes: 0,
    roundUpHours: false,
    position: '',
    isActive: true,
    description: '',
  });

  useEffect(() => {
    loadConfigs();
    loadPositions();
    loadEntryBlocking();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await api.get('/auto-close-config');
      setConfigs(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuracoes:', error);
      toast.error('Erro ao carregar configuracoes');
    } finally {
      setLoading(false);
    }
  };

  const loadPositions = async () => {
    try {
      // Civil não entra em janela de turno, então só funções militares aqui.
      const response = await api.get('/positions/active?personnelType=militar');
      setPositions(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcoes:', error);
    }
  };

  const loadEntryBlocking = async () => {
    try {
      const response = await api.get('/auto-close-config/entry-blocking');
      setEntryBlocking(response.data);
    } catch (error) {
      console.error('Erro ao carregar config de bloqueio:', error);
    }
  };

  const handleSaveEntryBlocking = async () => {
    setSavingBlocking(true);
    try {
      const response = await api.patch('/auto-close-config/entry-blocking', {
        enabled: entryBlocking.enabled,
        marginMinutes: parseInt(entryBlocking.marginMinutes) || 10,
      });
      setEntryBlocking(response.data);
      toast.success('Configuracao de bloqueio salva com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingBlocking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.closeTime) {
      toast.error('Nome e horario de fechamento sao obrigatorios');
      return;
    }

    // Validar formato do horario
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.closeTime)) {
      toast.error('Horario de fechamento invalido. Use o formato HH:MM');
      return;
    }

    try {
      const data = {
        ...formData,
        position: formData.position || null,
        entryTimeMin: formData.entryTimeMin || null,
        entryTimeMax: formData.entryTimeMax || null,
        earlyEntryMinutes: parseInt(formData.earlyEntryMinutes) || 0,
      };

      if (editingConfig) {
        await api.patch(`/auto-close-config/${editingConfig.id}`, data);
        toast.success('Configuracao atualizada com sucesso!');
      } else {
        await api.post('/auto-close-config', data);
        toast.success('Configuracao criada com sucesso!');
      }

      handleCloseModal();
      loadConfigs();
    } catch (error) {
      console.error('Erro ao salvar configuracao:', error);
      toast.error('Erro ao salvar configuracao: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      closeTime: config.closeTime?.substring(0, 5) || '',
      entryTimeMin: config.entryTimeMin?.substring(0, 5) || '',
      entryTimeMax: config.entryTimeMax?.substring(0, 5) || '',
      earlyEntryMinutes: config.earlyEntryMinutes || 0,
      roundUpHours: config.roundUpHours || false,
      position: config.position || '',
      isActive: config.isActive,
      description: config.description || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setFormData({
      name: '',
      closeTime: '',
      entryTimeMin: '',
      entryTimeMax: '',
      earlyEntryMinutes: 0,
      roundUpHours: false,
      position: '',
      isActive: true,
      description: '',
    });
  };

  const openDeleteModal = (config) => {
    setConfigToDelete(config);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingConfig) return;
    setShowDeleteModal(false);
    setConfigToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!configToDelete) return;

    setDeletingConfig(true);
    try {
      await api.delete(`/auto-close-config/${configToDelete.id}`);
      toast.success('Configuracao excluida com sucesso!');
      loadConfigs();
    } catch (error) {
      console.error('Erro ao excluir configuracao:', error);
      toast.error('Erro ao excluir configuracao: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeletingConfig(false);
      setShowDeleteModal(false);
      setConfigToDelete(null);
    }
  };

  const toggleActive = async (config) => {
    try {
      await api.patch(`/auto-close-config/${config.id}`, {
        isActive: !config.isActive,
      });
      toast.success(`Configuracao ${!config.isActive ? 'ativada' : 'desativada'} com sucesso!`);
      loadConfigs();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status');
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time.substring(0, 5);
  };

  // Separar configs gerais e especificas
  const generalConfigs = useMemo(() => configs.filter((c) => !c.position), [configs]);
  const specificConfigs = useMemo(() => configs.filter((c) => c.position), [configs]);

  const generalPagination = usePagination(generalConfigs, 10);
  const specificPagination = usePagination(specificConfigs, 10);

  if (loading) return (
    <>
      <Navbar />
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando configurações...</p>
      </div>
    </>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1>Fechamento Automatico de Ponto</h1>
            <p className="subtitle">Configure os horarios em que o sistema fecha automaticamente os registros de ponto</p>
          </div>
          <div className="header-actions">
            <button type="button" className="btn-primary" onClick={() => setShowModal(true)}>
              + Nova Configuracao
            </button>
          </div>
        </div>

        {/* Card explicativo */}
        <div className="info-card info-card-blue">
          <h3>Como funciona o fechamento automatico?</h3>
          <div className="info-card-body">
            <p>
              O sistema verifica <strong>a cada minuto</strong> se existem registros de ponto sem horario de saida
              e aplica automaticamente o horario de fechamento configurado.
            </p>
            <p>
              <strong>Configuracoes Gerais:</strong> Aplicam-se a TODOS os militares, exceto aqueles que possuem
              uma configuracao especifica para sua funcao.
            </p>
            <p>
              <strong>Configuracoes por Funcao:</strong> Quando uma funcao tem configuracao especifica,
              as configuracoes gerais NAO se aplicam aos militares dessa funcao.
            </p>
          </div>
        </div>

        {/* Bloqueio de Botao de Entrada */}
        <div className="info-card info-card-red">
          <h3>Bloqueio do Botão de Entrada</h3>
          <div className="info-card-body">
            <div className="blocking-controls">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entryBlocking.enabled || false}
                  onChange={(e) => setEntryBlocking({ ...entryBlocking, enabled: e.target.checked })}
                />
                <span>Habilitar bloqueio por horario</span>
              </label>
              {entryBlocking.enabled && (
                <div className="margin-input-group">
                  <label className="font-medium">Margem:</label>
                  <input
                    type="number"
                    value={entryBlocking.marginMinutes}
                    onChange={(e) => setEntryBlocking({ ...entryBlocking, marginMinutes: e.target.value })}
                    min="1"
                    max="120"
                    className="margin-input"
                  />
                  <span>minutos</span>
                </div>
              )}
              <button
                onClick={handleSaveEntryBlocking}
                disabled={savingBlocking}
                className="btn-primary"
                style={{ marginLeft: 'auto' }}
              >
                {savingBlocking ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
            {entryBlocking.enabled && (
              <div>
                <p>
                  Com margem de <strong>{entryBlocking.marginMinutes}min</strong>, o botao de registrar entrada no painel do fiscal
                  so ficara disponivel dentro de <strong>{entryBlocking.marginMinutes}min antes</strong> e <strong>{entryBlocking.marginMinutes}min depois</strong> do
                  horario minimo de entrada de cada turno.
                </p>
                <p>
                  <strong>Exemplo:</strong> Se o turno comeca as 07:00, o botao fica disponivel das{' '}
                  <strong>
                    {String(Math.floor((420 - (parseInt(entryBlocking.marginMinutes) || 10)) / 60)).padStart(2, '0')}:
                    {String((420 - (parseInt(entryBlocking.marginMinutes) || 10)) % 60).padStart(2, '0')}
                  </strong>
                  {' '}ate{' '}
                  <strong>
                    {String(Math.floor((420 + (parseInt(entryBlocking.marginMinutes) || 10)) / 60)).padStart(2, '0')}:
                    {String((420 + (parseInt(entryBlocking.marginMinutes) || 10)) % 60).padStart(2, '0')}
                  </strong>.
                </p>
              </div>
            )}
            {!entryBlocking.enabled && (
              <p>
                Quando desabilitado, o botao de entrada fica disponivel durante todo o periodo do turno (entrada minima ate entrada maxima).
              </p>
            )}
          </div>
        </div>

        {/* Exemplo dos turnos */}
        <div className="info-card info-card-yellow">
          <h3>Exemplo de configuração por turnos</h3>
          <div className="info-card-body">
            <table className="info-card-table">
              <thead>
                <tr>
                  <th>Turno</th>
                  <th>Horario do Turno</th>
                  <th>Fechamento</th>
                  <th>Entrada entre</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1 Turno (Manha)</td>
                  <td>07:00 as 12:20</td>
                  <td><strong>12:20</strong></td>
                  <td>07:00 - 12:00</td>
                </tr>
                <tr>
                  <td>2 Turno (Tarde)</td>
                  <td>13:00 as 18:20</td>
                  <td><strong>18:20</strong></td>
                  <td>13:00 - 18:00</td>
                </tr>
                <tr>
                  <td>3 Turno (Noite)</td>
                  <td>18:20 as 21:30</td>
                  <td><strong>21:30</strong></td>
                  <td>18:20 - 21:00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Configuracoes Gerais */}
        <div style={{ marginBottom: '32px' }}>
          <h2 className="section-title">
            Configuracoes Gerais
          </h2>
          <p className="section-subtitle">
            Estas configuracoes se aplicam a todos os militares que NAO possuem configuracao especifica.
          </p>
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Hora Fechamento</th>
                  <th>Entrada Min</th>
                  <th>Entrada Max</th>
                  <th>Tolerancia</th>
                  <th className="text-center">Arredondar</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {generalConfigs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center empty-state">
                      <strong>Nenhuma configuração geral cadastrada</strong>
                      Clique em "+ Nova Configuracao" para adicionar a primeira.
                    </td>
                  </tr>
                ) : (
                  generalPagination.pageItems.map((config) => (
                    <tr key={config.id}>
                      <td className="cell-primary">
                        {config.name}
                        {config.description && (
                          <span className="cell-sub">{config.description}</span>
                        )}
                      </td>
                      <td><span className="badge badge-rank">{formatTime(config.closeTime)}</span></td>
                      <td className="col-time">{formatTime(config.entryTimeMin)}</td>
                      <td className="col-time">{formatTime(config.entryTimeMax)}</td>
                      <td>{config.earlyEntryMinutes ? `${config.earlyEntryMinutes}min` : '-'}</td>
                      <td className="text-center">{config.roundUpHours ? 'Sim' : 'Nao'}</td>
                      <td className="text-center">
                        <button
                          onClick={() => toggleActive(config)}
                          className={`badge ${config.isActive ? 'badge-success' : 'badge-danger'}`}
                          style={{ cursor: 'pointer', border: 'none' }}
                        >
                          {config.isActive ? 'Ativo' : 'Inativo'}
                        </button>
                      </td>
                      <td className="text-center">
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger asChild>
                            <button className="icon-button" aria-label="Acoes">
                              <DotsVerticalIcon />
                            </button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Portal>
                            <DropdownMenu.Content className="dropdown-content" sideOffset={5}>
                              <DropdownMenu.Item className="dropdown-item" onSelect={() => handleEdit(config)}>
                                <Pencil1Icon /> Editar
                              </DropdownMenu.Item>
                              <DropdownMenu.Separator className="dropdown-separator" />
                              <DropdownMenu.Item
                                className="dropdown-item dropdown-item-danger"
                                onSelect={() => openDeleteModal(config)}
                              >
                                <TrashIcon /> Excluir
                              </DropdownMenu.Item>
                            </DropdownMenu.Content>
                          </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            id="autoclose-geral"
            page={generalPagination.page}
            totalPages={generalPagination.totalPages}
            total={generalPagination.total}
            from={generalPagination.from}
            to={generalPagination.to}
            pageSize={generalPagination.pageSize}
            onPageChange={generalPagination.setPage}
            onPageSizeChange={generalPagination.setPageSize}
            itemLabel="configurações"
          />
        </div>

        {/* Configuracoes Especificas por Funcao */}
        <div>
          <h2 className="section-title">
            Configuracoes por Funcao
          </h2>
          <p className="section-subtitle">
            Configuracoes especificas para determinadas funcoes. Militares com estas funcoes NAO serao afetados pelas configuracoes gerais.
          </p>
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Funcao</th>
                  <th>Hora Fechamento</th>
                  <th>Entrada Min</th>
                  <th>Entrada Max</th>
                  <th>Tolerancia</th>
                  <th className="text-center">Arredondar</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {specificConfigs.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center empty-state">
                      <strong>Nenhuma configuração por função cadastrada</strong>
                      Sem exceções: todo mundo segue as configurações gerais acima.
                    </td>
                  </tr>
                ) : (
                  specificPagination.pageItems.map((config) => (
                    <tr key={config.id}>
                      <td className="cell-primary">
                        {config.name}
                        {config.description && (
                          <span className="cell-sub">{config.description}</span>
                        )}
                      </td>
                      <td><span className="badge badge-fiscal">{config.position}</span></td>
                      <td><span className="badge badge-rank">{formatTime(config.closeTime)}</span></td>
                      <td className="col-time">{formatTime(config.entryTimeMin)}</td>
                      <td className="col-time">{formatTime(config.entryTimeMax)}</td>
                      <td>{config.earlyEntryMinutes ? `${config.earlyEntryMinutes}min` : '-'}</td>
                      <td className="text-center">{config.roundUpHours ? 'Sim' : 'Nao'}</td>
                      <td className="text-center">
                        <button
                          onClick={() => toggleActive(config)}
                          className={`badge ${config.isActive ? 'badge-success' : 'badge-danger'}`}
                          style={{ cursor: 'pointer', border: 'none' }}
                        >
                          {config.isActive ? 'Ativo' : 'Inativo'}
                        </button>
                      </td>
                      <td className="text-center">
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger asChild>
                            <button className="icon-button" aria-label="Acoes">
                              <DotsVerticalIcon />
                            </button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Portal>
                            <DropdownMenu.Content className="dropdown-content" sideOffset={5}>
                              <DropdownMenu.Item className="dropdown-item" onSelect={() => handleEdit(config)}>
                                <Pencil1Icon /> Editar
                              </DropdownMenu.Item>
                              <DropdownMenu.Separator className="dropdown-separator" />
                              <DropdownMenu.Item
                                className="dropdown-item dropdown-item-danger"
                                onSelect={() => openDeleteModal(config)}
                              >
                                <TrashIcon /> Excluir
                              </DropdownMenu.Item>
                            </DropdownMenu.Content>
                          </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            id="autoclose-funcao"
            page={specificPagination.page}
            totalPages={specificPagination.totalPages}
            total={specificPagination.total}
            from={specificPagination.from}
            to={specificPagination.to}
            pageSize={specificPagination.pageSize}
            onPageChange={specificPagination.setPage}
            onPageSizeChange={specificPagination.setPageSize}
            itemLabel="configurações"
          />
        </div>

        {/* Modal de Criacao/Edicao */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingConfig ? 'Editar Configuracao' : 'Nova Configuracao de Fechamento'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  x
                </button>
              </div>

              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  <label>Nome da Configuracao *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Fechamento 1 Turno (Manha)"
                    required
                  />
                  <small className="form-hint">
                    De um nome descritivo para identificar esta configuracao
                  </small>
                </div>

                <div className="form-group">
                  <label>Horario de Fechamento *</label>
                  <input
                    type="text"
                    value={formData.closeTime}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length > 5) value = value.slice(0, 5);
                      setFormData({ ...formData, closeTime: value });
                    }}
                    placeholder="12:20"
                    maxLength={5}
                    required
                  />
                  <small className="form-hint">
                    Horario em que o sistema colocara automaticamente a saida nos registros pendentes (formato HH:MM)
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Horario Entrada Minimo</label>
                    <input
                      type="text"
                      value={formData.entryTimeMin}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length > 5) value = value.slice(0, 5);
                        setFormData({ ...formData, entryTimeMin: value });
                      }}
                      placeholder="13:00"
                      maxLength={5}
                    />
                    <small className="form-hint">
                      Horario a partir do qual as horas comecam a ser contabilizadas
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Horario Entrada Maximo</label>
                    <input
                      type="text"
                      value={formData.entryTimeMax}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length > 5) value = value.slice(0, 5);
                        setFormData({ ...formData, entryTimeMax: value });
                      }}
                      placeholder="18:00"
                      maxLength={5}
                    />
                    <small className="form-hint">
                      So fecha registros com entrada ANTES deste horario
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tolerancia de Entrada Antecipada (minutos)</label>
                  <input
                    type="number"
                    value={formData.earlyEntryMinutes}
                    onChange={(e) => setFormData({ ...formData, earlyEntryMinutes: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                    max="60"
                  />
                  <small className="form-hint">
                    Permite que o militar entre ate X minutos antes do horario minimo.
                    <strong> As horas so serao contabilizadas a partir do horario minimo.</strong>
                    <br />
                    Ex: Se entrada minima = 13:00 e tolerancia = 10min, o militar pode entrar as 12:50,
                    mas as horas so contam a partir das 13:00.
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.roundUpHours}
                      onChange={(e) => setFormData({ ...formData, roundUpHours: e.target.checked })}
                    />
                    <span>Arredondar horas para cima</span>
                  </label>
                  <small className="form-hint">
                    Quando ativo, as horas serao arredondadas para a proxima hora inteira.
                    <br />
                    Ex: Se o turno vai de 07:00 as 12:20, em vez de contabilizar 5h, contabiliza <strong>6h</strong>.
                  </small>
                </div>

                <div className="form-group">
                  <label>Funcao Especifica (Opcional)</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  >
                    <option value="">-- Configuracao Geral (todos os militares) --</option>
                    {positions.map(position => (
                      <option key={position.id} value={position.name}>{position.name}</option>
                    ))}
                  </select>
                  <small className="form-hint">
                    Deixe vazio para aplicar a todos. Se selecionar uma funcao, esta configuracao
                    se aplicara APENAS aos militares dessa funcao, e as configuracoes gerais NAO se aplicarao a eles.
                  </small>
                </div>

                <div className="form-group">
                  <label>Descricao (Opcional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva o objetivo desta configuracao..."
                    rows={2}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>Configuracao Ativa</span>
                  </label>
                  <small className="form-hint">
                    Desative para pausar temporariamente esta configuracao sem excluí-la
                  </small>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingConfig ? 'Salvar Alteracoes' : 'Criar Configuracao'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Confirmacao de Exclusao */}
        {showDeleteModal && configToDelete && (
          <div className="modal-overlay" onClick={closeDeleteModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Excluir configuracao</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={closeDeleteModal}
                  disabled={deletingConfig}
                >
                  x
                </button>
              </div>

              <div className="confirmation-body">
                <p>
                  Tem certeza que deseja excluir a configuracao{' '}
                  <strong>{configToDelete.name}</strong>?
                </p>
                <p className="warning-text">
                  Atencao: Os registros de ponto que dependiam desta configuracao nao serao mais fechados automaticamente.
                </p>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deletingConfig}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deletingConfig}
                >
                  {deletingConfig ? 'Removendo...' : 'Sim, excluir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AutoCloseConfig;
